import { Box, Code, OrderedList, ListItem, Text, VStack } from '@chakra-ui/react';

import scimConfig from 'app/constants/scim';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import { ScimInstructionsProps, CopyCodeBlockProps, TrailingStep } from './ScimInstructions.types';

function CopyCodeBlock({ value, block = true, highlight = false }: CopyCodeBlockProps) {
  return (
    <Code
      p="8px"
      borderRadius="md"
      display={block ? 'block' : 'inline-block'}
      fontFamily="Inconsolata, monospace"
      fontSize="xs"
      wordBreak="break-all"
      bg={highlight ? 'red.50' : undefined}
    >
      <TextWithClipboard value={value} truncate={false} />
    </Code>
  );
}

const trailingSteps: TrailingStep[] = [
  {
    text: 'Test the connection to ensure that Microsoft Entra can authenticate with the SCIM API.',
  },
  { text: 'Create the provisioning configuration.' },
  {
    text: '(Optional) Under provisioning settings enable \u201cSend an email notification when a failure occurs\u201d and add the email addresses of the company IT admins or Aslan admins who should receive notifications on provisioning failures.',
  },
  {
    text: 'Under attribute mappings select \u201cProvision Microsoft Entra ID Groups\u201d and disable the group attribute.',
    subItems: ['No changes should be needed to the default User mapping.'],
  },
  {
    text: 'Under \u201cUsers and groups\u201d settings, add the groups or users that should be provisioned to the system via SCIM. You can choose to provision all users or specific groups/users based on your requirements.',
  },
  {
    text: 'Go back to overview and enable provisioning to start syncing users from Microsoft Entra to the system via the SCIM API.',
    subItems: [
      'Optionally try to sync one user with Provision on demand to test the configuration.',
    ],
  },
];

function ScimInstructions({
  clientId = '',
  clientSecret = '',
  compact = false,
}: ScimInstructionsProps) {
  return (
    <VStack align="stretch" spacing={compact ? '8px' : '12px'}>
      <Text fontWeight="semibold" fontSize={compact ? 'sm' : 'md'}>
        Microsoft Entra SCIM Integration
      </Text>
      <OrderedList spacing={compact ? '6px' : '8px'} fontSize={compact ? 'sm' : 'md'} pl="20px">
        <ListItem>
          Create an enterprise application in Microsoft Entra ID for SCIM provisioning.
        </ListItem>
        <ListItem>
          Select &ldquo;Integrate any other application you don&rsquo;t find in the gallery
          (Non-gallery)&rdquo;.
        </ListItem>
        <ListItem>
          Go to the &ldquo;Provisioning&rdquo; tab and create a new provisioning configuration.
        </ListItem>
        <ListItem>
          Set authentication method to &ldquo;OAuth2 client credentials grant&rdquo;
        </ListItem>
        <ListItem>
          <Text as="span">Enter the tenant URL: </Text>
          <Box mt="4px">
            <CopyCodeBlock value={scimConfig.tenantUrl} block={false} />
          </Box>
        </ListItem>
        <ListItem>
          <Text as="span">Enter the Token endpoint URL: </Text>
          <Box mt="4px">
            <CopyCodeBlock value={scimConfig.tokenEndpointUrl} block={false} />
          </Box>
        </ListItem>
        <ListItem>
          <Text as="span">
            Enter the Client ID and Client Secret obtained from the backoffice when creating the
            SCIM app client for the company.
          </Text>
          {clientId && clientSecret && (
            <VStack align="stretch" mt="8px" spacing="8px">
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb="4px">
                  Client ID:
                </Text>
                <CopyCodeBlock value={clientId} />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb="4px">
                  Client Secret:
                </Text>
                <CopyCodeBlock value={clientSecret} highlight />
              </Box>
            </VStack>
          )}
        </ListItem>
        {trailingSteps.map((step) => (
          <ListItem key={step.text}>
            {step.text}
            {step.subItems && (
              <OrderedList styleType="lower-alpha" mt="4px" pl="16px" spacing="4px">
                {step.subItems.map((subItem) => (
                  <ListItem key={`${step.text}-${subItem}`}>{subItem}</ListItem>
                ))}
              </OrderedList>
            )}
          </ListItem>
        ))}
      </OrderedList>
    </VStack>
  );
}

export { ScimInstructions };
